import React, { useState, useEffect, useRef } from "react";
import { Download, CalendarDays, Handshake, Flag, Target, TrendingDown } from "lucide-react";
import InfoTip from "../../globalComponents/ui/InfoTip";
import {
  BRAND, DANGER, SUCCESS, GUIDE, GRID,
  Card, CardHeadRow, HeadLeft, CardTitle, HeadRight, Legend, Toggle, Select,
  RateNote, TableWrap, Table, PaginationWrap, PageBtn, PageInfoText, PageNumberContainer,
  DownloadBtn, Pill, Muted, SubBar, DropPanel, SummaryGrid, SummaryCard, SummaryTopRow,
  SummaryLabel, SummaryIcon, SummaryValue, SummarySub,
} from "./NetZeroDashboard.styles";
import {
  num, pct, statusLabel, requiredFromNow,
  downloadExcel, downloadCategoryMatrix, CAT_COLORS,
} from "./netzero.utils";
import { PathwayChart } from "../ChartUtility/recharts/PathwayChart";
import { CategoryLinesChart } from "../ChartUtility/recharts/CategoryLinesChart";


function NotConfiguredCard() {
  return (
    <Card>
      <Muted>
        <div style={{ fontSize: 15, fontWeight: 600, color: "#334155", marginBottom: 6 }}>Net Zero isn't set up yet</div>
        Configure your net-zero targets in <b>Settings → Net Zero</b>, then come back to view the pathway.
      </Muted>
    </Card>
  );
}

function PathwayBody({ rows, baseYear, nearTermYear, netZeroYear, scopeLabel, view, plan }) {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const totalPages = Math.ceil(rows.length / pageSize);
  const paginatedRows = rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [rows]);

  return (
    <>
      {plan && (
        <RateNote>
          <span>
            From your {plan.fromYear} actual of <b style={{ color: "#4a4368" }}>{num(plan.fromValue)}</b> tCO₂e, you need to cut about
            {" "}<b>{plan.ratePct.toFixed(1)}%/yr</b> to reach net-zero by {plan.netZeroYear}.
          </span>
        </RateNote>
      )}
      {view === "chart" ? (
        <PathwayChart rows={rows} nearTermYear={nearTermYear} netZeroYear={netZeroYear} plan={plan} />
      ) : (
        <TableWrap style={{ display: "flex", flexDirection: "column" }}>
          <Table style={{ flex: 1 }}>
            <thead>
              <tr>
                <th>Year<InfoTip text={`Every year of the pathway, from the base year (${baseYear}) to the net-zero target year (${netZeroYear}).`} /></th>
                <th>Required min (tCO₂e)<InfoTip text="The most you're allowed to emit that year — the target ceiling. Base × (1 − rate × years since base), never below a 90% cut." /></th>
                <th>% reduction from base<InfoTip text="How far the ceiling sits below the base year: (Base − Required) ÷ Base. Starts at 0% and grows every year until it reaches 90%." /></th>
                <th>Annual reduction (tCO₂e)<InfoTip text="How much you must cut versus the previous year — the same slice of the original base — then 0 once the 90% target is reached." /></th>
                <th>Actual (tCO₂e)<InfoTip text={`Real emissions from the data (${scopeLabel}). Shown only for years that have data; blank (—) otherwise.`} /></th>
                <th>Status<InfoTip text="On Track if Actual ≤ Required that year (lower is better). Off Track shows how far Actual is above Required as a percent." /></th>
              </tr>
            </thead>
            <tbody>
              {paginatedRows.map((r) => (
                <tr key={r.year}>
                  <td>{r.year}</td>
                  <td>{num(r.required)}</td>
                  <td>
                    {pct(r.pctReduction)}
                    {r.actual !== null && r.required !== null && r.required > 0 && (
                      <span
                        style={{
                          display: "block",
                          fontSize: "11px",
                          fontWeight: "600",
                          marginTop: "2px",
                          color: r.status === "OFF_TRACK" ? "#dc2626" : "#166534"
                        }}
                      >
                        {r.status === "OFF_TRACK"
                          ? `+${(((r.actual - r.required) / r.required) * 100).toFixed(1)}%`
                          : `-${Math.abs(((r.actual - r.required) / r.required) * 100).toFixed(1)}%`}
                      </span>
                    )}
                  </td>
                  <td>{num(r.annualReduction)}</td>
                  <td>{num(r.actual)}</td>
                  <td>
                    {r.status === "NO_DATA" ? "—" : <Pill $status={r.status}>{statusLabel[r.status]}</Pill>}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          
          {totalPages > 1 && (
            <PaginationWrap>
              <PageInfoText>
                Showing {Math.min(rows.length, (currentPage - 1) * pageSize + 1)}–{Math.min(rows.length, currentPage * pageSize)} of {rows.length} years
              </PageInfoText>
              <PageNumberContainer>
                <PageBtn disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
                  &lsaquo; Prev
                </PageBtn>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <PageBtn key={page} $active={page === currentPage} onClick={() => setCurrentPage(page)}>
                    {page}
                  </PageBtn>
                ))}
                <PageBtn disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                  Next &rsaquo;
                </PageBtn>
              </PageNumberContainer>
            </PaginationWrap>
          )}
        </TableWrap>
      )}
    </>
  );
}

export function ScopePathwayPanel({ scope, data, loading }) {
  const [view, setView] = useState("chart");

  if (loading) return <Muted>Loading…</Muted>;
  if (!data) return <NotConfiguredCard />;

  const plan = requiredFromNow(data.rows, data.longTermTargetYear);

  return (
    <>
      <Card>
        <CardHeadRow>
          <HeadLeft>
            <CardTitle>Scope {scope} pathway ({data.baseYear}–{data.longTermTargetYear})</CardTitle>
            <DownloadBtn title="Download Excel" onClick={() => downloadExcel(data.rows, `Scope ${scope} Pathway`)}>
              <Download size={12} /> Download Excel
            </DownloadBtn>
          </HeadLeft>
          <HeadRight>
            {view === "chart" && (
              <Legend>
                <span><span style={{ width: 12, height: 2, background: BRAND, display: "inline-block" }} />Required</span>
                <span><span style={{ width: 8, height: 8, borderRadius: "50%", background: DANGER, display: "inline-block" }} />Actual</span>
              </Legend>
            )}
            <Toggle>
              <button type="button" className={view === "chart" ? "active" : ""} onClick={() => setView("chart")}>
                Chart
              </button>
              <button type="button" className={view === "table" ? "active" : ""} onClick={() => setView("table")}>
                Table
              </button>
            </Toggle>
          </HeadRight>
        </CardHeadRow>

        <PathwayBody
          rows={data.rows}
          baseYear={data.baseYear}
          nearTermYear={data.nearTermTargetYear}
          netZeroYear={data.longTermTargetYear}
          scopeLabel={`Scope ${scope} emission questions`}
          view={view}
          plan={plan}
        />
      </Card>
    </>
  );
}

function CategoryMultiSelect({ options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const toggle = (key) =>
    onChange(selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key]);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <Select as="button" type="button" onClick={() => setOpen((o) => !o)} style={{ textAlign: "left" }}>
        {selected.length ? `${selected.length} categor${selected.length === 1 ? "y" : "ies"} selected` : "Select categories"} ▾
      </Select>
      {open && (
        <DropPanel>
          {options.map((o) => (
            <label key={o.key}>
              <input type="checkbox" checked={selected.includes(o.key)} onChange={() => toggle(o.key)} style={{ accentColor: BRAND }} />
              {o.name}
            </label>
          ))}
        </DropPanel>
      )}
    </div>
  );
}

function CategoryMatrixTable({ cats }) {
  const base = cats[0]?.pathway || [];
  return (
    <TableWrap>
      <Table>
        <thead>
          <tr>
            <th>Year</th>
            {cats.map((c) => (
              <th key={c.categoryKey}>{c.name}<InfoTip text="Required target / actual emissions (tCO₂e)." /></th>
            ))}
          </tr>
        </thead>
        <tbody>
          {base.map((row) => (
            <tr key={row.year}>
              <td>{row.year}</td>
              {cats.map((c) => {
                const r = c.pathway.find((x) => x.year === row.year);
                return <td key={c.categoryKey}>{num(r?.required)} / {r?.actual == null ? "—" : num(r.actual)}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </Table>
    </TableWrap>
  );
}

export function Scope3Panel({ data, loading }) {
  const [view, setView] = useState("chart");
  const [mode, setMode] = useState("total");
  const [selectedCats, setSelectedCats] = useState([]);

  useEffect(() => {
    if (!data) return;
    const withData = (data.categories || []).filter((c) => c.baseEmissions > 0).map((c) => c.categoryKey);
    setSelectedCats(withData.slice(0, 4));
  }, [data]);

  if (loading) return <Muted>Loading…</Muted>;
  if (!data) return <NotConfiguredCard />;

  const totalRows = data.pathway || [];
  const totalPlan = requiredFromNow(totalRows, data.longTermTargetYear);
  const catsWithData = data.categories.filter((c) => c.baseEmissions > 0);
  const selectedCatsObjs = catsWithData.filter((c) => selectedCats.includes(c.categoryKey));
  const colorOf = (key) => CAT_COLORS[Math.max(0, catsWithData.findIndex((c) => c.categoryKey === key)) % CAT_COLORS.length];
  const onDownload = () =>
    mode === "total" ? downloadExcel(totalRows, "Scope 3 Total") : downloadCategoryMatrix(selectedCatsObjs);

  return (
    <>
      <Card style={{ marginBottom: 22 }}>
        <CardHeadRow>
          <HeadLeft>
            <CardTitle>Scope 3 pathway ({data.baseYear}–{data.longTermTargetYear})</CardTitle>
            <DownloadBtn title="Download Excel" onClick={onDownload}>
              <Download size={12} /> Download Excel
            </DownloadBtn>
          </HeadLeft>
          <HeadRight>
            {view === "chart" && mode === "total" && (
              <Legend>
                <span><span style={{ width: 12, height: 2, background: BRAND, display: "inline-block" }} />Required</span>
                <span><span style={{ width: 8, height: 8, borderRadius: "50%", background: DANGER, display: "inline-block" }} />Actual</span>
              </Legend>
            )}
            <Toggle>
              <button type="button" className={view === "chart" ? "active" : ""} onClick={() => setView("chart")}>Chart</button>
              <button type="button" className={view === "table" ? "active" : ""} onClick={() => setView("table")}>Table</button>
            </Toggle>
          </HeadRight>
        </CardHeadRow>

        <SubBar>
          <Toggle>
            <button type="button" className={mode === "total" ? "active" : ""} onClick={() => setMode("total")}>Total</button>
            <button type="button" className={mode === "category" ? "active" : ""} onClick={() => setMode("category")}>By category</button>
          </Toggle>
          {mode === "category" && (
            <CategoryMultiSelect
              options={catsWithData.map((c) => ({ key: c.categoryKey, name: `${c.categoryKey.replace("CAT_", "")} · ${c.name}` }))}
              selected={selectedCats}
              onChange={setSelectedCats}
            />
          )}
          <span style={{ fontSize: 12, color: "#94a3b8" }}>
            {mode === "total"
              ? "Aggregate of all near-term-included categories."
              : "Solid line = required target · dotted line = actual emissions."}
          </span>
        </SubBar>

        {mode === "total" ? (
          <PathwayBody
            rows={totalRows}
            baseYear={data.baseYear}
            nearTermYear={data.nearTermTargetYear}
            netZeroYear={data.longTermTargetYear}
            scopeLabel="Scope 3, all included categories"
            view={view}
            plan={totalPlan}
          />
        ) : selectedCatsObjs.length === 0 ? (
          <Muted>Select one or more categories above to plot.</Muted>
        ) : view === "chart" ? (
          <>
            <Legend style={{ padding: "12px 20px 0", flexWrap: "wrap" }}>
              {selectedCatsObjs.map((c) => (
                <span key={c.categoryKey}>
                  <span style={{ width: 12, height: 2, background: colorOf(c.categoryKey), display: "inline-block" }} />
                  {c.name}
                </span>
              ))}
            </Legend>
            <CategoryLinesChart
              cats={selectedCatsObjs}
              nearTermYear={data.nearTermTargetYear}
              netZeroYear={data.longTermTargetYear}
              colorOf={colorOf}
            />
          </>
        ) : (
          <CategoryMatrixTable cats={selectedCatsObjs} />
        )}
      </Card>

      <Card>
        <CardHeadRow>
          <HeadLeft>
            <CardTitle>Scope 3 Targets by Category (Base Year: {data.baseYear})</CardTitle>
          </HeadLeft>
        </CardHeadRow>
        <TableWrap>
          <Table>
            <colgroup>
              <col style={{ width: data.ntTargetRequired ? "35%" : "45%" }} />
              <col style={{ width: data.ntTargetRequired ? "15%" : "20%" }} />
              <col style={{ width: data.ntTargetRequired ? "10%" : "15%" }} />
              {data.ntTargetRequired && <col style={{ width: "8%" }} />}
              {data.ntTargetRequired && <col style={{ width: "12%" }} />}
              <col style={{ width: data.ntTargetRequired ? "8%" : "10%" }} />
              <col style={{ width: data.ntTargetRequired ? "12%" : "10%" }} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>Category<InfoTip text="The 15 categories defined by the GHG Protocol Corporate Value Chain Standard." /></th>
                <th>Base emissions (tCO₂e)<InfoTip text="The emissions for each category in the Scope 3 base year." /></th>
                <th>Share<InfoTip text="This category's share of total Scope 3 base footprint." /></th>
                {data.ntTargetRequired && <th style={{ textAlign: "center" }}>NT<InfoTip text="Included in near-term target boundary." /></th>}
                {data.ntTargetRequired && <th>NT Target (tCO₂e)<InfoTip text="Required target emissions for near-term target year (linear 2.5%/yr contraction from base)." /></th>}
                <th style={{ textAlign: "center" }}>LT<InfoTip text="Included in long-term target boundary." /></th>
                <th>LT Target (tCO₂e)<InfoTip text="Required target emissions for long-term target year (90% reduction from base)." /></th>
              </tr>
            </thead>
            <tbody>
              {catsWithData.length === 0 && (
                <tr>
                  <td colSpan={data.ntTargetRequired ? 7 : 5} style={{ textAlign: "center", color: "#94a3b8", padding: "24px 16px" }}>
                    No category emissions entered yet — add them in Settings → Net Zero.
                  </td>
                </tr>
              )}
              {catsWithData.map((r) => {
                const quiet = r.baseEmissions === 0;
                return (
                  <tr key={r.categoryKey}>
                    <td style={{ textAlign: "left", fontWeight: quiet ? 400 : 600, color: quiet ? "#94a3b8" : "#1e293b" }}>
                      <span style={{ color: "#94a3b8", marginRight: 8, fontVariantNumeric: "tabular-nums" }}>
                        {r.categoryKey.replace("CAT_", "").padStart(2, "0")}
                      </span>
                      {r.name}
                    </td>
                    <td>{num(r.baseEmissions)}</td>
                    <td style={{ color: quiet ? "#94a3b8" : "#64748b" }}>{quiet ? "—" : pct(r.share)}</td>
                    {data.ntTargetRequired && (
                      <td style={{ textAlign: "center" }}>
                        <input type="checkbox" checked={r.includeNt} disabled style={{ width: 14, height: 14, accentColor: BRAND }} />
                      </td>
                    )}
                    {data.ntTargetRequired && (
                      <td style={{ fontWeight: r.includeNt && !quiet ? 600 : 400, color: r.includeNt && !quiet ? "#1e293b" : "#94a3b8" }}>
                        {r.includeNt && !quiet ? num(r.nearTermTarget) : "—"}
                      </td>
                    )}
                    <td style={{ textAlign: "center" }}>
                      <input type="checkbox" checked={r.includeLt} disabled style={{ width: 14, height: 14, accentColor: BRAND }} />
                    </td>
                    <td style={{ fontWeight: r.includeLt && !quiet ? 600 : 400, color: r.includeLt && !quiet ? "#1e293b" : "#94a3b8" }}>
                      {r.includeLt && !quiet ? num(r.longTermTarget) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot style={{ display: catsWithData.length === 0 ? "none" : "table-footer-group" }}>
              <tr style={{ background: "#f8fafc", fontWeight: 700, borderTop: `1px solid ${GRID}` }}>
                <td style={{ textAlign: "left" }}>Total</td>
                <td>{num(data.totalBaseEmissions)}</td>
                <td>100%</td>
                {data.ntTargetRequired && <td style={{ textAlign: "center" }}>{(data.coverageNt * 100).toFixed(0)}%</td>}
                {data.ntTargetRequired && <td>{num(data.categories.reduce((s, r) => s + (r.includeNt ? r.nearTermTarget : 0), 0))}</td>}
                <td style={{ textAlign: "center" }}>{(data.coverageLt * 100).toFixed(0)}%</td>
                <td>{num(data.categories.reduce((s, r) => s + (r.includeLt ? r.longTermTarget : 0), 0))}</td>
              </tr>
            </tfoot>
          </Table>
        </TableWrap>
      </Card>
      <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 8, textAlign: "left" }}>
        {data.ntTargetRequired
          ? `Near-term target = base × (1 − 2.5%/yr × years to ${data.nearTermTargetYear}). `
          : ""}
        Long-term target = base × 10% (a 90% cut). Rows with no emissions or not included read as "—".
      </div>
    </>
  );
}

export function ConfigSummaryCards({ setting }) {
  if (!setting) return null;

  const cards = [
    setting.baseYearScope12 != null && { label: "Base year", sub: "Scope 1 & 2", value: setting.baseYearScope12, accent: BRAND, icon: <CalendarDays size={16} strokeWidth={1.9} /> },
    setting.baseYearScope3 != null && { label: "Base year", sub: "Scope 3", value: setting.baseYearScope3, accent: BRAND, icon: <CalendarDays size={16} strokeWidth={1.9} /> },
    setting.netZeroCommitmentYear != null && { label: "Commitment year", value: setting.netZeroCommitmentYear, accent: GUIDE, icon: <Handshake size={16} strokeWidth={1.9} /> },
    setting.nearTermTargetYear != null && { label: "Near-term target", value: setting.nearTermTargetYear, accent: "#a0651a", icon: <Flag size={16} strokeWidth={1.9} /> },
    setting.longTermTargetYear != null && { label: "Net-zero target", value: setting.longTermTargetYear, accent: SUCCESS, icon: <Target size={16} strokeWidth={1.9} /> },
    setting.ambitionOverridePercentage != null && { label: "Ambition rate", value: `${setting.ambitionOverridePercentage}%/yr`, accent: DANGER, icon: <TrendingDown size={16} strokeWidth={1.9} /> },
  ].filter(Boolean);

  return (
    <SummaryGrid>
      {cards.map((c) => (
        <SummaryCard key={`${c.label}-${c.sub || ""}`} $accent={c.accent}>
          <SummaryTopRow>
            <SummaryLabel>{c.label}</SummaryLabel>
            <SummaryIcon>{c.icon}</SummaryIcon>
          </SummaryTopRow>
          <SummaryValue>{c.value}</SummaryValue>
          <SummarySub>{c.sub || " "}</SummarySub>
        </SummaryCard>
      ))}
    </SummaryGrid>
  );
}
